import React from 'react';
import { Link } from 'react-router-dom';
import { LuArrowRight } from 'react-icons/lu';
import CensorBar from './CensorBar';
import PlinthCard from './PlinthCard';
import EditorialBlock from './EditorialBlock';
import HairlineDivider from './HairlineDivider';
import ChyronLabel from './ChyronLabel';
import EmptyState from './EmptyState';
import CategoryScorePanel from './CategoryScorePanel';
import { VECTOR_DISPLAY_LABELS, isTaxonomyVerdict } from '../../contracts/oracleApi';
import { ROUTES } from '../../contracts/routes';
import { COPY } from '../../contracts/copyMap';
import { formatPercentile, formatScore10 } from '../create/format';

// Shared affiliate-tracking helper (kept for the legacy recommendations branch).
export function trackAffiliate(product, vector) {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'affiliate_click', {
        vector,
        brand: product.brand,
        product: product.product_name,
      });
    }
  } catch {
    // analytics is best-effort — do not block navigation.
  }
  return true;
}

function legacyFormatScore(score) {
  if (typeof score !== 'number') return String(score || '');
  return score.toFixed ? score.toFixed(1) : String(score);
}

// ── Shared sub-sections (percentile + geometry) reused by both branches ──────
const PercentileSection = ({ data }) =>
  typeof data.learned_percentile === 'number' ? (
    <section
      aria-label={COPY.VERDICT_PERCENTILE_LABEL}
      className="flex flex-col items-center gap-3"
    >
      <CensorBar mode="RANK" value={formatPercentile(data.learned_percentile)} size="lg" />
      <div className="font-display uppercase text-xs tracking-[0.24em] text-bone-dim">
        {COPY.VERDICT_PERCENTILE_LABEL}
      </div>
      <p className="font-display italic text-sm text-bone-dim text-center max-w-sm m-0">
        {COPY.VERDICT_PERCENTILE_NOTE}
      </p>
    </section>
  ) : null;

const GeometrySection = ({ data }) =>
  data.geometry && Array.isArray(data.geometry.metrics) && data.geometry.metrics.length > 0 ? (
    <section aria-label={COPY.VERDICT_GEOMETRY_HEADLINE}>
      <EditorialBlock
        headline={COPY.VERDICT_GEOMETRY_HEADLINE}
        subtitle={COPY.VERDICT_GEOMETRY_SUBTITLE}
        size="M"
      />
      <div className="mt-4 flex flex-col">
        {data.geometry.metrics.map((m) => {
          const strong = m.band === 'strong';
          return (
            <div
              key={m.key}
              className="flex items-center justify-between gap-4 py-3 border-b border-[rgba(255,255,255,0.06)]"
            >
              <span className="font-display uppercase text-sm tracking-[0.18em] text-bone">{m.label}</span>
              <span className="flex items-baseline gap-3">
                <span className="font-display text-xl text-bone-dim tabular-nums">{m.score}</span>
                <span
                  className={`font-censor font-bold uppercase text-[11px] tracking-[0.12em] ${
                    strong ? 'text-aurum' : 'text-bone-dim'
                  }`}
                >
                  {strong ? COPY.GEOMETRY_BAND_STRONG : COPY.GEOMETRY_BAND_ADDRESSABLE}
                </span>
              </span>
            </div>
          );
        })}
      </div>
      <p className="font-display italic text-sm text-bone-dim mt-3 m-0">{COPY.VERDICT_GEOMETRY_NOTE}</p>
    </section>
  ) : null;

// ── New taxonomy verdict ─────────────────────────────────────────────────────
const TaxonomyVerdict = ({ data, footer, onImageError }) => {
  return (
    <div className="w-full max-w-3xl flex flex-col items-stretch gap-12">
      <PlinthCard
        imageUrl={data.processed_image_url}
        alt="processed portrait"
        censorBarOverlay={{ mode: 'SCORE', value: formatScore10(data.verdict.overall_score) }}
        className="w-full max-w-md self-center"
        onImageError={onImageError}
      />

      <EditorialBlock headline={COPY.VERDICT_HEADLINE} size="L" />

      {/* WHAT YOU CAN CHANGE — expandable categories → subcategory scores + notes */}
      <section aria-label={COPY.VERDICT_CATEGORIES_HEADLINE}>
        <EditorialBlock
          headline={COPY.VERDICT_CATEGORIES_HEADLINE}
          subtitle={COPY.VERDICT_CATEGORIES_SUBTITLE}
          size="M"
        />
        <CategoryScorePanel verdict={data.verdict} className="mt-4" />
      </section>

      <PercentileSection data={data} />
      <GeometrySection data={data} />

      <HairlineDivider />

      {/* HOW YOUR ROUTINE IS PERFORMING — reference to the Routine page */}
      <section
        aria-label={COPY.VERDICT_ROUTINE_REF_HEADLINE}
        className="bg-[#161615] border border-hairline-soft rounded-card p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <div className="font-display uppercase text-xs tracking-[0.24em] text-[#E8E2D6]">
            {COPY.VERDICT_ROUTINE_REF_HEADLINE}
          </div>
          <p className="font-display italic text-sm text-bone-dim mt-1.5 m-0">
            {COPY.VERDICT_ROUTINE_REF_SUBTITLE}
          </p>
        </div>
        <Link
          to={ROUTES.ROUTINE}
          className="inline-flex items-center gap-2.5 shrink-0 no-underline rounded-field border border-[rgba(201,164,92,0.45)] px-[18px] py-[11px] font-display text-xs tracking-[0.2em] text-aurum-bright hover:border-aurum-bright transition-colors duration-200"
        >
          <span>{COPY.VERDICT_OPEN_ROUTINE_CTA}</span>
          <LuArrowRight size={15} strokeWidth={1.5} />
        </Link>
      </section>

      {footer && <div className="flex justify-center mt-12">{footer}</div>}
    </div>
  );
};

// ── Legacy verdict (frozen pre-taxonomy scans) ───────────────────────────────
const LegacyVerdict = ({ data, footer, onImageError }) => (
  <div className="w-full max-w-3xl flex flex-col items-stretch gap-12">
    <PlinthCard
      imageUrl={data.processed_image_url}
      alt="processed portrait"
      censorBarOverlay={{ mode: 'SCORE', value: legacyFormatScore(data.verdict.overall_score) }}
      className="w-full max-w-md self-center"
      onImageError={onImageError}
    />

    <EditorialBlock headline={COPY.VERDICT_HEADLINE} size="L" />

    <section aria-label={COPY.VERDICT_VECTORS_HEADLINE}>
      <div className="grid grid-cols-2 gap-6 mt-4">
        {(data.verdict.vectors || []).map((v) => (
          <div key={v.name} className="flex flex-col gap-2">
            <CensorBar mode="LABEL" value={VECTOR_DISPLAY_LABELS[v.name] || v.name.toUpperCase()} size="sm" />
            <div className="font-display text-3xl">{v.score}</div>
          </div>
        ))}
      </div>
    </section>

    <PercentileSection data={data} />
    <GeometrySection data={data} />

    <HairlineDivider />

    <EditorialBlock
      headline={COPY.VERDICT_RECOMMENDATIONS_HEADLINE}
      subtitle={COPY.VERDICT_RECOMMENDATIONS_SUBTITLE}
      size="M"
    />

    {(data.recommendations || []).map((rec) => (
      <section key={rec.vector} className="my-12 relative pb-12">
        <ChyronLabel value={VECTOR_DISPLAY_LABELS[rec.vector] || rec.vector.toUpperCase()} />
        {rec.products.length === 0 ? (
          <EmptyState
            headline={COPY.EMPTY_VECTOR_CATALOG_HEADLINE}
            subtitle={COPY.EMPTY_VECTOR_CATALOG_SUBTITLE}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-4">
            {rec.products.map((p) => (
              <a
                key={p.affiliate_url}
                href={p.affiliate_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackAffiliate(p, rec.vector)}
                className="block"
              >
                <PlinthCard imageUrl={p.image_url} alt={p.brand} />
                <div className="mt-2 font-display text-sm">{p.brand} — {p.product_name}</div>
                <div className="font-censor text-xs uppercase">{p.price_range}</div>
              </a>
            ))}
          </div>
        )}
      </section>
    ))}

    {footer && <div className="flex justify-center mt-12">{footer}</div>}
  </div>
);

// Shared post-Oracle verdict body. New scans use the doc-driven taxonomy panel;
// frozen old scans (no `subcategories`) fall back to the legacy 8-vector layout.
const VerdictDisplay = ({ data, footer, onImageError }) =>
  isTaxonomyVerdict(data.verdict)
    ? <TaxonomyVerdict data={data} footer={footer} onImageError={onImageError} />
    : <LegacyVerdict data={data} footer={footer} onImageError={onImageError} />;

export default VerdictDisplay;
